/**
 * URL transport shared by every filter codec: deflate when it pays, base64url,
 * and UUID compaction. Kept apart from any one filter shape so the entity list's
 * flat tuples and the events list's condition tree can ride the same encoding.
 */

import pako from "pako";

const te = new TextEncoder();
const td = new TextDecoder();

function b64urlFromBytes(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function bytesFromB64url(b64: string): Uint8Array {
  const pad = b64.length % 4 === 2 ? "==" : b64.length % 4 === 3 ? "=" : "";
  const s = b64.replace(/-/g, "+").replace(/_/g, "/") + pad;
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function deflateRawToB64url(s: string): string {
  const deflated = pako.deflateRaw(te.encode(s));
  return b64urlFromBytes(deflated);
}
function inflateRawFromB64url(b64: string): string {
  const inflated = pako.inflateRaw(bytesFromB64url(b64));
  return td.decode(inflated);
}
function rawToB64url(s: string): string {
  return b64urlFromBytes(te.encode(s));
}
function rawFromB64url(b64: string): string {
  return td.decode(bytesFromB64url(b64));
}

// Adaptive: prefix "1" for compressed, "0" for plain
export function encodeAdaptive(s: string): string {
  const c = deflateRawToB64url(s);
  const p = rawToB64url(s);
  return c.length + 1 < p.length ? "1" + c : "0" + p;
}
export function decodeAdaptive(s: string): string {
  if (!s) return "";
  const tag = s[0];
  const body = s.slice(1);
  if (tag === "1") return inflateRawFromB64url(body);
  if (tag === "0") return rawFromB64url(body);
  // Back-compat
  try {
    return inflateRawFromB64url(s);
  } catch {
    return rawFromB64url(s);
  }
}

// ---------- UUID compaction (36 -> 22 chars) ----------
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, "");
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}
function bytesToUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}
export function uuidToB64(uuid: string): string {
  return b64urlFromBytes(uuidToBytes(uuid));
}
export function b64ToUuid(b64: string): string {
  return bytesToUuid(bytesFromB64url(b64));
}

// ---------- Stable tiny IDs (generated on parse) ----------
export function fnv1a32(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
